import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { TextNode } from "lexical";
import { useEffect, useSyncExternalStore } from "react";

import { $createMergeTagNode } from "./MergeTagNode";
import {
  getConnectorMergeTagRegistryVersion,
  getMergeTagByIdentifier,
  subscribeConnectorMergeTagRegistry,
  type MergeTagRegistryEntry,
} from "../mergeTagDefinitions";
import { DISCOVER_HEADING_TAG } from "../productContexts";
import { $ancestorProductContext } from "./ProductBlockPlugin";

import { mergeTagsService } from "@/services/mergeTags";

interface MergeTagAutoDetectPluginProps {
  /**
   * Restricts which tags become chips to this identifier set, so detection
   * matches the set the picker/typeahead offer for the surface. When omitted,
   * any recognized tag is detected (the TextComposerV2 default).
   */
  allowedIdentifiers?: Set<string>;
  /**
   * Tags the host offers that the shared registry doesn't know (e.g. a grid
   * card's `product.*` fields), so a stored value re-chips them on load.
   */
  extraTags?: ReadonlyMap<string, MergeTagRegistryEntry>;
}

/**
 * Watches for TextNodes containing recognized `{{identifier}}` patterns and
 * replaces them with MergeTagNodes. Handles both paste and manual typing.
 */
export function MergeTagAutoDetectPlugin({
  allowedIdentifiers,
  extraTags,
}: MergeTagAutoDetectPluginProps = {}): null {
  const [editor] = useLexicalComposerContext();

  // Connector metadata identifiers load async (installed-connectors fetch), so
  // the initial content parse can run before they're known — a connector tag
  // stays plain text. Re-registering on a version bump marks existing TextNodes
  // dirty (Lexical's registerNodeTransform), re-running detection so the tag
  // chips once its identifier resolves.
  const connectorRegistryVersion = useSyncExternalStore(
    subscribeConnectorMergeTagRegistry,
    getConnectorMergeTagRegistryVersion,
  );

  useEffect(() => {
    return editor.registerNodeTransform(TextNode, (node) => {
      if (!node.isSimpleText()) return;

      const text = node.getTextContent();
      const match = mergeTagsService.matchFirstMergeTag(text);
      if (!match) return;

      const entry =
        getMergeTagByIdentifier(match.identifier) ??
        extraTags?.get(match.identifier);
      if (!entry) return;
      // The Discover headline is bound by its section; elsewhere it stays text.
      if (
        match.identifier === DISCOVER_HEADING_TAG &&
        $ancestorProductContext(node)?.getModel() !== "discover"
      ) {
        return;
      }

      // When the host scopes the input, only chip identifiers it offers, so a
      // pasted/typed tag outside the offered set stays as plain text.
      if (allowedIdentifiers && !allowedIdentifiers.has(match.identifier)) {
        return;
      }

      const matchStart = match.index;
      const matchEnd = match.index + match.length;

      let targetNode: TextNode = node;

      if (matchStart > 0) {
        targetNode = node.splitText(matchStart)[1];
      }

      const endOffset = matchEnd - matchStart;
      if (endOffset < targetNode.getTextContentSize()) {
        targetNode.splitText(endOffset);
      }

      const mergeTagNode = $createMergeTagNode(
        entry.identifier,
        entry.label,
        match.filters,
      );
      targetNode.replace(mergeTagNode);
    });
  }, [editor, allowedIdentifiers, extraTags, connectorRegistryVersion]);

  return null;
}
